import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, Pause, RotateCcw, EyeOff, Clock,
  ChevronUp, ChevronDown, Move, Plus, Minus
} from 'lucide-react';

const POSITION_OPTIONS = [
  { id: 'top_left', name: 'Top Left' },
  { id: 'top_right', name: 'Top Right' },
  { id: 'bottom_left', name: 'Bottom Left' },
  { id: 'bottom_right', name: 'Bottom Right' },
  { id: 'center', name: 'Center' },
];

// Custom Dropdown component
const Dropdown = ({ trigger, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
      {isOpen && (
        <div className="absolute bottom-full mb-2 right-0 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 min-w-[140px] z-50">
          {React.Children.map(children, child =>
            React.cloneElement(child, { onClick: () => { child.props.onClick?.(); setIsOpen(false); } })
          )}
        </div>
      )}
    </div>
  );
};

export default function TimerPlaybackControls({
  timer,
  status,
  displayTime,
  onStart,
  onPause,
  onReset,
  onHide,
  onAddTime,
  currentPosition,
  onPositionChange
}) {
  const [isMinimized, setIsMinimized] = useState(false);

  if (!timer) return null;

  const isRunning = status === 'running';
  const isPaused = status === 'paused';
  const isIdle = status === 'idle';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed bottom-24 sm:bottom-28 left-1/2 -translate-x-1/2 z-[60] w-[95vw] sm:w-auto max-w-md"
      >
        <motion.div
          className="bg-[#001233]/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-4 py-2 cursor-pointer border-b border-white/10"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium text-white truncate max-w-32">
                {timer.name}
              </span>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <div className={`w-2 h-2 rounded-full ${
                isRunning ? 'bg-green-500 animate-pulse' :
                isPaused ? 'bg-amber-500' : 'bg-gray-500'
              }`} />

              <span className="font-mono text-lg text-white font-bold">
                {displayTime}
              </span>

              <button className="p-1 hover:bg-white/10 rounded">
                {isMinimized ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Controls */}
          <AnimatePresence>
            {!isMinimized && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="flex items-center gap-1.5 sm:gap-2 p-2 sm:p-3 flex-wrap">
                  {/* Play/Pause */}
                  {isRunning ? (
                    <button
                      onClick={onPause}
                      className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm rounded-lg transition-colors"
                    >
                      <Pause className="w-4 h-4" />
                      Pause
                    </button>
                  ) : (
                    <button
                      onClick={onStart}
                      className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs sm:text-sm rounded-lg transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      {isPaused ? 'Resume' : 'Start'}
                    </button>
                  )}

                  {/* Reset */}
                  <button
                    onClick={onReset}
                    disabled={isIdle && timer.timer_type !== 'countup'}
                    className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 border border-white/20 text-white text-xs sm:text-sm rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </button>

                  {/* Add/Remove Time - for countdown_duration in any state */}
                  {timer.timer_type === 'countdown_duration' && onAddTime && (
                    <div className="flex items-center gap-1 border border-white/20 rounded-lg overflow-hidden">
                      <button
                        onClick={() => onAddTime(-60)}
                        className="flex items-center gap-0.5 px-2 py-1.5 text-white text-xs sm:text-sm hover:bg-white/10 transition-colors"
                        title="Remove 1 minute"
                      >
                        <Minus className="w-3.5 h-3.5" />
                        <span className="text-xs">1m</span>
                      </button>
                      <div className="w-px h-5 bg-white/20" />
                      <button
                        onClick={() => onAddTime(60)}
                        className="flex items-center gap-0.5 px-2 py-1.5 text-white text-xs sm:text-sm hover:bg-white/10 transition-colors"
                        title="Add 1 minute"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span className="text-xs">1m</span>
                      </button>
                      <div className="w-px h-5 bg-white/20" />
                      <button
                        onClick={() => onAddTime(300)}
                        className="flex items-center gap-0.5 px-2 py-1.5 text-white text-xs sm:text-sm hover:bg-white/10 transition-colors"
                        title="Add 5 minutes"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span className="text-xs">5m</span>
                      </button>
                    </div>
                  )}

                  {/* Hide */}
                  <button
                    onClick={onHide}
                    className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 border border-red-500/50 text-red-400 text-xs sm:text-sm rounded-lg hover:bg-red-500/20 transition-colors"
                  >
                    <EyeOff className="w-4 h-4" />
                    Hide
                  </button>

                  {/* Position */}
                  <Dropdown
                    trigger={
                      <button className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 border border-white/20 text-white text-xs sm:text-sm rounded-lg hover:bg-white/10 transition-colors">
                        <Move className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                        <span className="hidden sm:inline">Position</span>
                      </button>
                    }
                  >
                    {POSITION_OPTIONS.map((pos) => (
                      <button
                        key={pos.id}
                        onClick={() => onPositionChange?.(pos.id)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                          currentPosition === pos.id ? 'text-cyan-400' : 'text-gray-300'
                        }`}
                      >
                        {pos.name}
                        {currentPosition === pos.id && ' ✓'}
                      </button>
                    ))}
                  </Dropdown>
                </div>

                {/* Timer type indicator */}
                <div className="px-3 pb-2 text-xs text-gray-500">
                  {timer.timer_type === 'countdown_duration' && 'Countdown Timer'}
                  {timer.timer_type === 'countdown_datetime' && 'Countdown to Date'}
                  {timer.timer_type === 'countup' && 'Count-Up Timer'}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
